import { authFetch } from "../../authFetch";
import { prependBasename } from "../../utils/routingUtils";

// Only body-less, idempotent methods are safe to replay. Replaying a POST
// because a *legitimate* 404 ("No live evaluation found") came back from the
// un-prefixed path could create a duplicate side effect.
const REPLAYABLE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

export const fetchApi = async (
  path: string,
  init?: RequestInit
): Promise<Response> => {
  const url = prependBasename(path);
  const response = await authFetch(url, init);
  const method = (init?.method ?? "GET").toUpperCase();
  // A 404 with the basename prefix usually means the server is mounted at the
  // root and the prefix is wrong, so retry the un-prefixed path.
  if (
    response.status === 404 &&
    url !== path &&
    REPLAYABLE_METHODS.has(method)
  ) {
    return authFetch(path, init);
  }
  return response;
};

type ValidationIssue = { loc?: unknown; msg?: unknown };

/**
 * Turn a FastAPI error `detail` into a readable string.
 *
 * FastAPI returns `detail` as a string for `HTTPException`, but as an array of
 * `{loc, msg, type}` objects for 422 request-validation errors. Passing that
 * array to `new Error(...)` renders "[object Object]" in the toast, so flatten
 * it into a human-readable message.
 */
export const extractApiErrorMessage = (
  detail: unknown,
  fallback: string
): string => {
  if (typeof detail === "string" && detail.trim().length > 0) {
    return detail;
  }
  if (Array.isArray(detail)) {
    const messages = detail
      .map((issue): string | null => {
        if (typeof issue === "string") {
          return issue;
        }
        if (!issue || typeof issue !== "object") {
          return null;
        }
        const { loc, msg } = issue as ValidationIssue;
        if (typeof msg !== "string") {
          return null;
        }
        const field = Array.isArray(loc)
          ? loc.filter((part) => part !== "body").join(".")
          : "";
        return field ? `${field}: ${msg}` : msg;
      })
      .filter((message): message is string => message !== null);
    if (messages.length > 0) {
      return messages.join("; ");
    }
  }
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    return JSON.stringify(detail);
  }
  return fallback;
};
